import React from 'react'
import { BrowserRouter } from 'react-router-dom'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import App from '@components/App'
import WebsiteRouter from '@/router/website'
import ScrollRestoration from '@components/ScrollRestoration'
import { resolveLiveTheme } from '@/theme/index.js'
import { APPEARANCE_PREVIEW_QUERY } from '@/theme/tokens.js'

// CSS imports are handled in client-entry.js (Vite entry point)

// Suppress browser extension errors that appear in console
// These occur when extensions intercept requests but fail to send async responses
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        const errorMessage = event.reason?.message || event.reason || ''
        if (typeof errorMessage === 'string' && 
            errorMessage.includes('A listener indicated an asynchronous response by returning true, but the message channel closed')) {
            event.preventDefault() // Suppress the error
            return
        }
    })
    
    // Prevent pinch zoom on touch devices (Safari ignores viewport meta tag)
    document.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false })
    document.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false })
    document.addEventListener('gestureend', (e) => e.preventDefault(), { passive: false })
    
    // Prevent multi-touch zoom (for browsers that don't support gesture events)
    document.addEventListener('touchmove', (e) => {
        if (e.touches.length > 1) {
            e.preventDefault()
        }
    }, { passive: false })
    
    // Reset zoom on orientation change (iOS can stay zoomed after rotation)
    let lastOrientation = window.orientation
    
    const resetZoom = () => {
        const viewport = document.querySelector('meta[name="viewport"]')
        if (viewport) {
            // Temporarily allow scaling, then lock it again to force reset
            const originalContent = viewport.getAttribute('content')
            viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover')
            
            // Force layout recalculation
            document.body.style.opacity = '0.999'
            
            setTimeout(() => {
                viewport.setAttribute('content', originalContent)
                document.body.style.opacity = ''
            }, 10)
        }
    }
    
    // Use orientationchange event (works on iOS Safari)
    window.addEventListener('orientationchange', () => {
        // Small delay to let the rotation complete
        setTimeout(resetZoom, 100)
    })
    
    // Also listen for resize as a fallback (fires after orientation change)
    window.addEventListener('resize', () => {
        const currentOrientation = window.orientation
        if (currentOrientation !== lastOrientation) {
            lastOrientation = currentOrientation
            setTimeout(resetZoom, 100)
        }
    })
}

const container = document.getElementById('app')
if (!container) {
    throw new Error('Root element not found')
}

// Content is injected by SSR into window.__CONTENT__
// Wrap hydration in async function to wait for window.__CONTENT__ if needed
(async function hydrateApp() {
    // Define url and urlBase inside the async function to ensure location is available
    const urlBase = typeof location !== 'undefined' ? (location.protocol + '//' + location.host + '/') : ''
    const url = typeof location !== 'undefined' ? (location.pathname + location.search) : '/'
    // Wait for window.__CONTENT__ to be available (inline script should run before module, but ensure it's there)
    // Module scripts are deferred, so inline scripts should already have run
    let waitCount = 0
    const maxWait = 100 // Maximum iterations to wait (100ms)
    while ((document.readyState === 'loading' || (typeof window !== 'undefined' && window.__CONTENT__ === undefined)) && waitCount < maxWait) {
        if (document.readyState === 'loading') {
            await new Promise(resolve => {
                if (document.readyState === 'loading') {
                    document.addEventListener('DOMContentLoaded', resolve, { once: true })
                } else {
                    resolve()
                }
            })
        } else {
            // If DOM is ready but __CONTENT__ is still undefined, wait a tick
            await new Promise(resolve => setTimeout(resolve, 1))
        }
        waitCount++
    }

    // Compile the theme through the same module the server used, so hydration
    // cannot disagree with the server-rendered markup.
    const { theme } = resolveLiveTheme(window.__CONTENT__ || {})

    const context = {
        urlBase,
        url,
        setStatus() {},
        content: window.__CONTENT__,
        theme
    }

    // Note: Analytics pageview tracking is handled in the website layout component
    // to properly track SPA route changes (pathname changes only)

    // A subsite (/~slug) routes everything below its own prefix.
    const basename = window.__CONTENT__?.subsite?.basename || undefined

    const tree = (
        <BrowserRouter basename={basename}>
            <HelmetProvider>
                <App context={context}>
                    <ScrollRestoration />
                    <WebsiteRouter />
                </App>
            </HelmetProvider>
        </BrowserRouter>
    )

    // Appearance preview skips SSR (empty #app); mount fresh instead of hydrating.
    const isAppearancePreview = new URLSearchParams(location.search).get(APPEARANCE_PREVIEW_QUERY) === '1'
    if (isAppearancePreview) {
        createRoot(container).render(tree)
    } else {
        hydrateRoot(container, tree)
    }
})()
